import React from "react";
import { Box, Link } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { MARKETING_LANGUAGES, MARKETING_LOCALES, MarketingLanguage } from "../../marketing-content/marketingLocales";
import { getMarketingPageTranslations } from "../../marketing-content/marketingPageTranslations";
import { getMarketingTranslations } from "../../marketing-content/marketingTranslations";
import { rememberMarketingLanguageChoice } from "../../marketing-content/marketingLanguageSuggestion";
import { useLandingTheme } from "../landing-page/LandingThemeContext";
import { useIsHydrated } from "../landing-page/handoff/useAppUrl";
import "../../fonts/localization-fonts/languageNames.css";

/**
 * The manual language choice. Real crawlable links keep language selection usable before hydration and with
 * JS disabled. Every marketing page shows it in the footer (`footerColor`, the footer's text colour):
 * centered as the footer's last element, with a list that opens upward because the footer is the end of
 * the page. Without `footerColor` it is laid out for the nav, beside the logo, where no page puts it now.
 */
export const MarketingLanguageSelector = React.memo(function MarketingLanguageSelector({ language, footerColor }: { language: MarketingLanguage; footerColor?: string }) {
  const { pathname, search, hash } = useLocation();
  const isHydrated = useIsHydrated();
  const translations = getMarketingPageTranslations(pathname);
  const theme = useLandingTheme();
  const inFooter = footerColor !== undefined;
  const color = footerColor ?? (theme.isDark || theme.navOnDark ? "white" : theme.color.heading);
  // display: table shrinks to the label and centers with auto margins.
  const placement = inFooter ? { display: "table", mx: "auto", mb: "16px" } : { mx: { base: "12px", md: "20px" } };
  return (
    <Box as="details" position="relative" fontFamily={'system-ui, "Jungle Language Names Devanagari", sans-serif'} color={color} {...placement}>
      <Box as="summary" aria-label={getMarketingTranslations(language).site.languageSelector} cursor="pointer" minH="44px" display="list-item" py="11px" px="6px" fontSize="14px" borderRadius="8px" _focusVisible={{ outline: "2px solid currentColor", outlineOffset: "2px" }}>
        {MARKETING_LOCALES[language].name}
      </Box>
      <Box position="absolute" {...(inFooter ? { insetInlineStart: 0, bottom: "100%" } : { insetInlineEnd: 0, top: "100%" })} zIndex={30} bg={theme.color.pageBg} color={theme.color.heading} border={`1px solid ${theme.color.border}`} boxShadow="0 12px 32px rgba(0,0,0,.16)" borderRadius="16px" p="8px" minW="190px" maxH="min(65vh, 420px)" overflowY="auto">
        {MARKETING_LANGUAGES.filter(locale => translations[locale]).map(locale => (
          <Link key={locale} href={`${translations[locale]}${isHydrated ? search + hash : ""}`} hrefLang={MARKETING_LOCALES[locale].hreflang} lang={MARKETING_LOCALES[locale].htmlLang} dir={MARKETING_LOCALES[locale].direction} onClick={rememberMarketingLanguageChoice} aria-current={locale === language ? "page" : undefined} display="block" py="10px" px="12px" borderRadius="8px" fontSize="15px" fontWeight={locale === language ? 700 : 400} _hover={{ bg: theme.color.accentSoft, textDecoration: "none" }}>
            {MARKETING_LOCALES[locale].name}
          </Link>
        ))}
      </Box>
    </Box>
  );
});
